import React, { useState, useEffect, useMemo, useTransition, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Button,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Pagination,
  Skeleton,
  useMediaQuery,
  useTheme,
  Fade,
  TableSortLabel,
  Divider,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip,
  Switch,
  FormControlLabel,
  Popover,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  GetApp as ExportIcon,
  PictureAsPdf as PdfIcon,
  Description as CsvIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TransferWithinAStation as TransferIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { motion, AnimatePresence } from 'framer-motion';
import { getStoredUser } from '../../../api/client';
import ThirteenthMonthPayslipDialog from './components/ThirteenthMonthPayslipDialog';
import { export13thMonthSummary, export13thMonthSummaryCSV, exportToPDF, exportToPrint, formatDataForExport } from '../../../utils/exportUtils';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
//import { getThirteenthMonthSummary, getAllThirteenthMonthSummaryJSON, getAllThirteenthMonthSummaryCSV, getPayrollById, getThirteenthMonthById } from '../../../api/payroll';
import { format } from 'date-fns';
import EditableComponent from 'components/EditableComponent';
import useUpdateThirteenthMonthPayHook from './hooks/useUpdateThirteenthMonthPayHook';
import UpdateThirteenthMonthPayButton from './components/updateThirteenthMonthPayButton';
import { parse } from 'date-fns';
import getCutoffPeriod from './functions/getCutOffPeriod';

import { getThirteenthMonthSummary, getAllThirteenthMonthSummaryJSON, getAllThirteenthMonthSummaryCSV, getPayrollById, getThirteenthMonthById, removeThirteenMonthPersonnel } from '../../../api/payroll';

// Mock data for stores (this would come from API in production)
const ALL_STORES = [
  'SM Cebuano North',
  'SM Cebuano Seaside',
  'Jollibee Food 1',
  'Jollibee Food 2',
  'Sinjay Surfing Mega Mall R01',
  'JOLLIBEE BANAWE',
  'JOLLIBEE ORTIGAS',
  'JOLLIBEE MAKATI'
];

// Mock data for 13th month pay records (employee data for a specific payroll record)
const generateMockEmployeeData = (recordId) => {
  const names = [
    'DELA CRUZ, JUAN LUNA',
    'DELA CRUZ, JUAN',
    'SANTOS, MARIA CLARA',
    'REYES, JOSE RIZAL',
    'GARCIA, PEDRO SANTOS',
    'LOPEZ, ANA MARIE',
    'MARTINEZ, CARLOS JUAN',
    'RODRIGUEZ, ISABEL MARIA',
    'FERNANDEZ, MIGUEL ANGEL',
    'GONZALEZ, ROSA LINDA',
    'HERNANDEZ, DIEGO LUIS',
    'BAUTISTA, ANNA MARIE',
    'VILLANUEVA, RICARDO',
    'RAMOS, JOSEPHINE'
  ];

  // Use recordId as a seed for consistent random generation
  const seed = recordId || 1;
  const seededRandom = (index) => {
    const x = Math.sin(seed + index) * 10000;
    return x - Math.floor(x);
  };

  return names.map((name, index) => {
    // Generate consistent cutoff amounts based on seed
    const cutoffs = {
      OCT1631: Math.floor(seededRandom(index * 8 + 1) * 300) + 200,
      NOV115: Math.floor(seededRandom(index * 8 + 2) * 300) + 200,
      NOV1630: Math.floor(seededRandom(index * 8 + 3) * 300) + 200,
      DEC115: Math.floor(seededRandom(index * 8 + 4) * 300) + 200,
      DEC1631: Math.floor(seededRandom(index * 8 + 5) * 300) + 200,
      JAN115: Math.floor(seededRandom(index * 8 + 6) * 300) + 200,
      JAN1631: Math.floor(seededRandom(index * 8 + 7) * 300) + 200,
      FEB115: Math.floor(seededRandom(index * 8 + 8) * 300) + 200,
      FEB1628: Math.floor(seededRandom(index * 8 + 9) * 300) + 200,
      MARCH1631: Math.floor(seededRandom(index * 8 + 9) * 300) + 200
    };

    const totalBasicPay = Object.values(cutoffs).reduce((sum, val) => sum + val, 0);
    const total13thMP = totalBasicPay / 12;

    return {
      id: index + 1,
      employeeId: `EMP${1000 + index}`,
      employeeName: name,
      ...cutoffs,
      totalBasicPay: totalBasicPay,
      thirteenthMonthPay: total13thMP,
      isEnabled: seededRandom(index * 2 + 10) > 0.5, // Consistent enabled status
      status: seededRandom(index * 2 + 11) > 0.5 ? 'enabled' : 'disabled',
      store: ALL_STORES[index % ALL_STORES.length]
    };
  });
};

// FilterRow component - manages its own local state to prevent parent re-renders
const FilterRow = React.memo(({ 
  cutoffPeriods, 
  appliedFilters, 
  onApplyFilters, 
  onClearFilters, 
  hasActiveFilters,
  isPending 
}) => {
  // Local state for filter inputs - changes here don't affect parent
  const [localFilters, setLocalFilters] = useState({
    employeeName: ''
  });

  // Sync local filters with applied filters when they change externally
  useEffect(() => {
    setLocalFilters({
      employeeName: appliedFilters.employeeName || ''
    });
  }, [appliedFilters.employeeName]);

  

  // Local filter handlers - only update local state
  const handleColumnFilterChange = (column, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  // Check if local filters differ from applied filters
  const hasPendingChanges = useMemo(() => {
    return localFilters.employeeName !== appliedFilters.employeeName;
  }, [localFilters.employeeName, appliedFilters.employeeName]);

  const handleApply = () => {
    onApplyFilters(localFilters);
  };

  const handleClear = () => {
    setLocalFilters({
      employeeName: ''
    });
    onClearFilters();
  };

  return (
    <TableRow sx={{
      '& .MuiTableCell-root': {
        backgroundColor: '#f0f4f8',
        borderBottom: '1px solid #e5e7eb',
      }
    }}>
      <TableCell
        sx={{
          position: 'sticky',
          top: 57,
          left: 0,
          zIndex: 5,
          background: '#f0f4f8',
        }}
      >
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title="Apply Filters">
            <IconButton
              size="small"
              onClick={handleApply}
              disabled={isPending || !hasPendingChanges}
              sx={{
                color: hasPendingChanges ? '#12C1DC' : '#9ca3af',
                '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' },
                '&.Mui-disabled': { color: '#d1d5db' }
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {hasActiveFilters && (
            <Tooltip title="Clear all filters">
              <IconButton
                size="small"
                onClick={handleClear}
                sx={{
                  color: '#ef4444',
                  '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' }
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell
        sx={{
          position: 'sticky',
          top: 57,
          left: 100,
          zIndex: 5,
          bgcolor: '#f0f4f8',
        }}
      >
        <TextField
          size="small"
          placeholder="Filter name..."
          value={localFilters.employeeName}
          onChange={(e) => handleColumnFilterChange('employeeName', e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleApply();
            }
          }}
          sx={{
            minWidth: '150px',
            '& .MuiOutlinedInput-root': {
              bgcolor: 'white',
              '& fieldset': { borderColor: '#d1d5db' },
              '&:hover fieldset': { borderColor: '#12C1DC' },
              '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
            },
            '& .MuiInputBase-input': {
              py: 0.75,
              fontSize: '0.8rem'
            }
          }}
          InputProps={{
            endAdornment: localFilters.employeeName && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => handleColumnFilterChange('employeeName', '')}
                  sx={{ p: 0.25 }}
                >
                  <ClearIcon sx={{ fontSize: '0.875rem' }} />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </TableCell>
      {cutoffPeriods.map((cutoffLabel, idx) => (
        <TableCell key={idx} align="right" sx={{ top: 57 }} />
      ))}
      <TableCell align="right" sx={{ top: 57 }} />
      <TableCell align="right" sx={{ top: 57 }} />
    </TableRow>
  );
});

FilterRow.displayName = 'FilterRow';

// Helper function to normalize name for flexible matching
// Handles "Last, First", "First Last", and variations with/without commas
const normalizeName = (name) => {
  if (!name) return '';
  // Remove commas, extra spaces, and convert to lowercase
  return name
    .replace(/,/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
};

// Helper function to check if search query matches employee name flexibly
// Supports: "First Last", "Last, First", "Last First", etc.
const matchesEmployeeName = (employeeName, searchQuery) => {
  // If there's no search query, don't filter (return true)
  if (!searchQuery || !searchQuery.trim()) return true;
  
  // If there's a search query but no employee name, exclude the record (return false)
  if (!employeeName) return false;

  // Normalize both the employee name and search query
  const normalizedEmployeeName = normalizeName(employeeName);
  const normalizedSearchQuery = normalizeName(searchQuery);

  // Split search query into individual terms
  const searchTerms = normalizedSearchQuery.split(/\s+/).filter(term => term.length > 0);
  
  if (searchTerms.length === 0) return true;

  // Check if all search terms appear in the normalized employee name
  // This allows matching regardless of order or comma placement
  return searchTerms.every(term => normalizedEmployeeName.includes(term));
};

const ViewThirteenthMonthPayroll = () => {
  const { id: recordId } = useParams();
  const navigate = useNavigate();
  // State management for employee records
  const [records, setRecords] = useState([]);
  const [stores, setStores] = useState(ALL_STORES);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [user, setUser] = useState(null);

  const [payrollStatus, setPayrollStatus] = useState('processing');

  //update records hook
  const { updateThirteenthMonthPay, getUpdatedBasicPay, getAllUpdates, submitChanges, removeChanges, newThirteenthMonthPay } = useUpdateThirteenthMonthPayHook();
  const [editing, toggleEditing] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [tableContainerScroll, setTableContainerScroll] = useState({ top: 0, left: 0 });

  // Dialog states
  const [payslipDialogOpen, setPayslipDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [selectedEmployeeForPopover, setSelectedEmployeeForPopover] = useState(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [dataSummary, setDataSummary] = useState([]);
  const [selectedEmployeeForTransfer, setSelectedEmployeeForTransfer] = useState(null);
  const [selectedEmployeeForRemove, setSelectedEmployeeForRemove] = useState(null);
  const [selectedTransferStore, setSelectedTransferStore] = useState('');

  // Enhanced table state
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('employeeName');

  // Column filters state (applied filters)
  const [columnFilters, setColumnFilters] = useState({
    employeeName: ''
  });

  // Theme and responsive
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // scroll to previous position after updates and clear loading spinner
  useEffect(() => {
    if (!isPending) {
      let scrollContainer = document.getElementById('table-container');
      if (scrollContainer) {
        scrollContainer.scrollTop = tableContainerScroll.top;
        scrollContainer.scrollLeft = tableContainerScroll.left;
      }
      // Clear table loading spinner when transition completes
      // Add small delay to ensure spinner is visible even for fast transitions
      const timer = setTimeout(() => {
        setTableLoading(false);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isPending])
  
  useEffect(() => {
    const loadUser = () => {
      try {
        const storedUser = getStoredUser();
        setUser(storedUser);

        if (storedUser) {
          let availableStores = [];

          // Super admin gets all stores
          if (storedUser.role === 'super-admin') {
            availableStores = ALL_STORES.map(store => ({
              value: store,
              label: store
            }));
          } else {
            // Check for group access or store access
            const groupAccess = storedUser.groupAccess || [];
            const storeAccess = storedUser.storeAccess || [];

            // Filter stores based on access
            if (groupAccess.length > 0 || storeAccess.length > 0) {
              availableStores = ALL_STORES
                .filter(store => {
                  // Check if store is in user's access list
                  return storeAccess.includes(store) ||
                    groupAccess.some(group => store.toLowerCase().includes(group.toLowerCase()));
                })
                .map(store => ({
                  value: store,
                  label: store
                }));
            }
          }

          setStores(availableStores);

          // If no stores available, show warning
          if (availableStores.length === 0) {
            setError('You do not have access to any stores. Please contact your administrator.');
          }
        }
      } catch (err) {
        console.error('Error loading user:', err);
        setError('Failed to load user permissions');
      }
    };

    loadUser();
  }, []);

  // Fetch 13th month employee records
  const fetchRecords = async () => {
    setTableLoading(true);
    setError(null);

    try {
      // Call the backend to get thirteenth month summary data
      const response = await getThirteenthMonthSummary(recordId);
      const payrollResponse = await getThirteenthMonthById(recordId);
      setPayrollStatus(payrollResponse.data.status || 'processing');
      if (response.data.length > 0) {
        const summaries = response.data;
        setDataSummary(summaries)
        const formattedRecords = (summaries || []).map((summary, idx) => {
          return {
            id: idx + 1,
            _id: summary._id,
            employeeId: summary.employee_id,
            employeeName: summary.employee_name,
            totalBasicPay: summary.total_basic_pay || 0,
            thirteenthMonthPay: summary.total_13th_month || 0,
            date_ranges: summary.date_ranges,
            isEnabled: true,
            status: 'enabled',
            store: '',
          };
        });
        setRecords(formattedRecords);
        setTotalItems(formattedRecords.length);
        setDataLoaded(true);
        setLoading(false);
        enqueueSnackbar('13th month employee records loaded successfully', { variant: 'success' });
      }
      else {
        alert('No records found')
        navigate('/payroll/13thmonth')
      }

    } catch (err) {
      navigate('/payroll/13thmonth')
      setError('Failed to fetch 13th month employee records');
      console.error('Error fetching records:', err);
      setLoading(false);
      enqueueSnackbar('Failed to fetch 13th month employee records', { variant: 'error' });

    } finally {
      setTableLoading(false);
    }
  };
  // Fetch 13th month employee records when recordId changes
  useEffect(() => {
    if (recordId) {
      fetchRecords();
    }
  }, []);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchRecords();
      enqueueSnackbar('Data refreshed successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error refreshing records:', err);
      enqueueSnackbar('Failed to refresh data', { variant: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  // Handle toggle enable/disable
  const handleToggleEnable = (record) => {
    setRecords(prevRecords =>
      prevRecords.map(r =>
        r.id === record.id
          ? { ...r, isEnabled: !r.isEnabled, status: !r.isEnabled ? 'enabled' : 'disabled' }
          : r
      )
    );
    enqueueSnackbar(
      `13th month payslip ${!record.isEnabled ? 'enabled' : 'disabled'} for ${record.employeeName}`,
      { variant: 'success' }
    );
  };

  // Handle open popover
  const handleOpenPopover = (event, record) => {
    event.stopPropagation();
    event.preventDefault();
    setPopoverAnchor(event.currentTarget);
    setSelectedEmployeeForPopover(record);
  };

  // Handle close popover
  const handleClosePopover = () => {
    setPopoverAnchor(null);
    setSelectedEmployeeForPopover(null);
  };

  // Handle edit record
  const handleEditRecord = (record) => {
    setEditData({ ...record }); // Create a copy of the record for editing
    setEditDialogOpen(true);
    handleClosePopover();
  };

  // Handle transfer employee
  const handleTransferEmployee = (record) => {
    setSelectedEmployeeForTransfer(record);
    setSelectedTransferStore(''); // Reset store selection
    setTransferDialogOpen(true);
    handleClosePopover();
  };

  // Handle remove employee
  const handleRemoveEmployee = (record) => {
    setSelectedEmployeeForRemove(record);
    setRemoveDialogOpen(true);
    handleClosePopover();
  };

  // Handle confirm transfer
  const handleConfirmTransfer = () => {
    if (selectedEmployeeForTransfer && selectedTransferStore) {
      // Update employee record with new store
      setRecords(prevRecords =>
        prevRecords.map(r =>
          r.id === selectedEmployeeForTransfer.id
            ? { ...r, store: selectedTransferStore }
            : r
        )
      );
      enqueueSnackbar(`${selectedEmployeeForTransfer.employeeName} has been transferred to ${selectedTransferStore}`, { variant: 'success' });
      setTransferDialogOpen(false);
      setSelectedEmployeeForTransfer(null);
      setSelectedTransferStore('');
    } else if (!selectedTransferStore) {
      enqueueSnackbar('Please select a store to transfer the employee to', { variant: 'warning' });
    }
  };

  // Handle confirm remove
  const handleConfirmRemove = async () => {
    if (selectedEmployeeForRemove) {
      const data = {
        id: recordId,
        name: selectedEmployeeForRemove.employeeName
      }
      const result = await removeThirteenMonthPersonnel(data)
      if (result.status === "success") {
        await fetchRecords()
        enqueueSnackbar(`${selectedEmployeeForRemove.employeeName} has been removed from payroll`, { variant: 'success' });
        setRemoveDialogOpen(false);
        setSelectedEmployeeForRemove(null);
      }
      else {
        alert("Something went wrong please try again later")
      }

    }
  };

  // Handle cancel transfer
  const handleCancelTransfer = () => {
    setTransferDialogOpen(false);
    setSelectedEmployeeForTransfer(null);
    setSelectedTransferStore('');
  };

  // Handle cancel remove
  const handleCancelRemove = () => {
    setRemoveDialogOpen(false);
    setSelectedEmployeeForRemove(null);
  };

  // Map cutoff period labels to date_range objects for display purposes
  // This ensures we can display date ranges in headers even if not all records have the same periods
  const cutoffPeriodToDateRange = useMemo(() => {
    const map = {};
    dataSummary.forEach(record => {
      record.date_ranges?.forEach(dr => {
        const label = getCutoffPeriod(dr);
        if (!map[label]) {
          map[label] = dr;
        }
      });
    });
    return map;
  }, [dataSummary]);

  // Use getCutoffPeriod for consistent label generation across the component
  // Sort chronologically by payroll_from date to ensure columns appear in order
  const cutoffPeriods = useMemo(() => {
    const uniquePeriods = Array.from(
      new Set(
        dataSummary.flatMap(record =>
          record.date_ranges?.map(dr => getCutoffPeriod(dr)) || []
        )
      )
    );
    
    // Sort by payroll_from date from the mapped date_range objects
    return uniquePeriods.sort((a, b) => {
      const dateA = cutoffPeriodToDateRange[a]?.payroll_from;
      const dateB = cutoffPeriodToDateRange[b]?.payroll_from;
      if (!dateA || !dateB) return 0;
      return new Date(dateA) - new Date(dateB);
    });
  }, [dataSummary, cutoffPeriodToDateRange]);

  // Handle save edited record
  const handleSaveEdit = () => {
    if (editData) {
      // Calculate the cutoff values from the edit data
      const cutoffValues = [
        editData.OCT1631 || 0,
        editData.NOV115 || 0,
        editData.NOV1630 || 0,
        editData.DEC115 || 0,
        editData.DEC1631 || 0,
        editData.JAN115 || 0,
        editData.JAN1631 || 0,
        editData.FEB115 || 0,
        editData.FEB1628 || 0
      ];

      const totalBasicPay = cutoffValues.reduce((sum, val) => sum + val, 0);
      const thirteenthMonthPay = totalBasicPay / 12;

      setRecords(prevRecords =>
        prevRecords.map(r =>
          r.id === editData.id
            ? {
              ...editData,
              totalBasicPay: totalBasicPay,
              thirteenthMonthPay: thirteenthMonthPay
            }
            : r
        )
      );
      setEditDialogOpen(false);
      setEditData(null);
      enqueueSnackbar('Payroll record updated successfully', { variant: 'success' });
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditData(null);
  };

  // Handle edit data change
  const handleEditDataChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  // Handle view payslip
  const handleViewPayslip = (record) => {
    if (!record.isEnabled) {
      enqueueSnackbar('Please enable 13th month payslip first', { variant: 'warning' });
      return;
    }
    setSelectedEmployee(record);
    setPayslipDialogOpen(true);
  };

  const handleClosePayslipDialog = () => {
    setPayslipDialogOpen(false);
    setTimeout(() => {
      setSelectedEmployee(null);
    }, 100);
  };


  // Sorting - use startTransition to make sorting non-blocking for better UI responsiveness
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setTableLoading(true);
    startTransition(() => {
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    });
  };

  // Apply filters handler - receives filters from FilterRow component
  const handleApplyFilters = useCallback((filters) => {
    setTableLoading(true);
    startTransition(() => {
      setColumnFilters(filters);
      setPage(1);
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setTableLoading(true);
    const emptyFilters = {
      employeeName: ''
    };
    startTransition(() => {
      setColumnFilters(emptyFilters);
      setPage(1);
    });
  }, []);

  const hasActiveFilters = useMemo(() => {
    return columnFilters.employeeName !== '' && columnFilters.employeeName !== undefined && columnFilters.employeeName !== null;
  }, [columnFilters]);

  // Helper function to get cutoff value for a record - uses getCutoffPeriod for consistent label generation
  const getCutoffValueForRecord = (record, cutoffLabel) => {
    const match = record.date_ranges?.find(dr => {
      const label = getCutoffPeriod(dr);
      return label === cutoffLabel;
    });
    return match?.basic_pay ?? 0;
  };

  // Filtering with column filters
  const filteredRecords = useMemo(() => {
    let filtered = [...records];

    // Employee Name filter - supports flexible name formats
    if (columnFilters.employeeName) {
      filtered = filtered.filter(record =>
        matchesEmployeeName(record.employeeName, columnFilters.employeeName)
      );
    }

    // Sorting - default is alphabetical by employee name
    filtered.sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return filtered;
  }, [records, order, orderBy, columnFilters]);
  const maxCutoffs = Math.max(...dataSummary.map(r => r.date_ranges.length));
  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / rowsPerPage);
  const paginatedRecords = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredRecords.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRecords, page, rowsPerPage]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(1);
  };

  // Export handlers
  const handleExportCSV = async () => {
    enqueueSnackbar('Preparing for downloading. This may take a moment...', { variant: 'info' });

    try {
      let records = await getAllThirteenthMonthSummaryCSV(recordId);
      let url = URL.createObjectURL(records)
      window.open(url)
      URL.revokeObjectURL(url)

    } catch (err) {
      enqueueSnackbar('Failed to prepare data for downloading', { variant: 'error' });
    }


    enqueueSnackbar('CSV exported successfully', { variant: 'success' });
  };

  const handleExportPDF = () => {
    if (!filteredRecords || filteredRecords.length === 0) {
      enqueueSnackbar('No data to export', { variant: 'warning' });
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Add title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('13th Month Pay Report', 14, 20);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Store: ${selectedStore}`, 14, 28);
      doc.text(`Period: ${fromDate.format('MM/DD/YYYY')} to ${toDate.format('MM/DD/YYYY')}`, 14, 34);
      doc.text(`Generated on: ${dayjs().format('MM/DD/YYYY hh:mm A')}`, 14, 40);

      // Prepare table data
      const tableData = filteredRecords.map(record => [
        record.employeeName,
        record.employeeId,
        record.totalBasicPay.toFixed(2),
        record.thirteenthMonthPay.toFixed(2),
        record.isEnabled ? 'Enabled' : 'Disabled'
      ]);

      doc.autoTable({
        head: [[
          'Employee Name',
          'Employee ID',
          'Total Basic Pay',
          'Total 13th MP',
          'Status'
        ]],
        body: tableData,
        startY: 45,
        styles: {
          fontSize: 10,
          cellPadding: 3,
          overflow: 'linebreak'
        },
        headStyles: {
          fillColor: [18, 193, 220],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'center'
        },
        columnStyles: {
          0: { cellWidth: 60, halign: 'left' },
          1: { cellWidth: 40, halign: 'center' },
          2: { cellWidth: 40, halign: 'right', fontStyle: 'bold' },
          3: { cellWidth: 40, halign: 'right', fontStyle: 'bold' },
          4: { cellWidth: 30, halign: 'center' }
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250]
        }
      });

      // Add page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
      }

      const dateRange = `${fromDate.format('YYYY-MM-DD')}_to_${toDate.format('YYYY-MM-DD')}`;
      doc.save(`13th_month_pay_${selectedStore}_${dateRange}.pdf`);
      enqueueSnackbar('PDF exported successfully', { variant: 'success' });
    } catch (error) {
      console.error('PDF export error:', error);
      enqueueSnackbar('Failed to export PDF', { variant: 'error' });
    }
  };

  // Safely calculate largestIndex - returns -1 if dataSummary is empty
  const largestIndex = dataSummary.length > 0
    ? dataSummary.reduce(
        (maxIdx, current, idx, arr) =>
          current.date_ranges.length > arr[maxIdx].date_ranges.length ? idx : maxIdx,
        0
      )
    : -1;

  const handlePrint = async () => {

    try {
      enqueueSnackbar('Preparing for printing', { variant: 'info' });
      let records = await getAllThirteenthMonthSummaryJSON(recordId);
      let payroll = await getThirteenthMonthById(recordId);
      let updatedRecords = records.filter((rec) => rec)
      if (!records || records.length === 0) {
        enqueueSnackbar('No data available for printing', { variant: 'warning' });
        return;
      }

      let colors = ['#FFCDD2', '#C5CAE9'];
      let colorPerYear = {}

      enqueueSnackbar('Data ready for printing', { variant: 'success' });


      console.log('============= PRINT DATA PREPARATION =============');

      const printData = updatedRecords.map(record => {
        let data = {
          'Names': record.employee_name,
        };

        //attaching colors to cutoffs, append _bgcolor- on the data object
        (record.date_ranges || []).forEach((date_range) => {

          console.log('processing date range', date_range);
          let year = format(new Date(date_range.payroll_from), 'yyyy');

          //if theres no color assigned to the year, assign one
          if (!colorPerYear[year]) {
            colorPerYear[year] = colors[Object.keys(colorPerYear).length % colors.length];
          }

          let title = String(`${format(new Date(date_range.payroll_from), 'MMMdd')}${format(new Date(date_range.payroll_to), 'dd')}-${year}`).toUpperCase();
          //  let key = `${new Date(date_range.payroll_from).toDateString()} - ${new Date(date_range.payroll_to).toDateString()}`;
          data[title] = parseFloat(date_range.basic_pay).toFixed(2);

          const color = colorPerYear[year];


          data[`_bgcolor-${title}`] = color;
        });

        data.Total = parseFloat(record.total_13th_month).toFixed(2);

        // Add dynamic cutoff fields


        return data;
      })


      console.log('print data', printData)

      export13thMonthSummary(printData, `${payroll.data.name || ""} 13th Month Pay Report`);

    } catch (error) {
      console.error('Print error:', error);
      enqueueSnackbar('Failed to print data', { variant: 'error' });
    }
  };


  const handlePrintCSV = async () => {

    try {
      enqueueSnackbar('Preparing for printing', { variant: 'info' });
      let records = await getAllThirteenthMonthSummaryJSON(recordId);
      let payroll = await getThirteenthMonthById(recordId);
      let updatedRecords = records.filter((rec) => rec)
      if (!records || records.length === 0) {
        enqueueSnackbar('No data available for printing', { variant: 'warning' });
        return;
      }

      let colors = ['#FFCDD2', '#C5CAE9'];
      let years = {}

      enqueueSnackbar('Data ready for printing', { variant: 'success' });
      const printData = updatedRecords.map(record => {
        let data = {
          'Names': record.employee_name,
        };

        (record.date_ranges || []).forEach((date_range) => {
          let year = format(new Date(date_range.payroll_from), 'yyyy');
          if (!years[year]) {
            years[year] = colors[Object.keys(years).length % colors.length];
          }
          let title = String(`${format(new Date(date_range.payroll_from), 'MMMdd')}${format(new Date(date_range.payroll_to), 'dd')}-${year}`).toUpperCase();
          //  let key = `${new Date(date_range.payroll_from).toDateString()} - ${new Date(date_range.payroll_to).toDateString()}`;
          data[title] = parseFloat(date_range.basic_pay).toFixed(2);
          data[`_bgcolor-${title}`] = years[year];
        });
        console.log(record)
        data.Total = parseFloat(record.total_13th_month).toFixed(2);


        // Add dynamic cutoff fields


        return data;
      })

      export13thMonthSummaryCSV(printData, `${payroll.data.name || ""} 13th Month Pay Report`);

    } catch (error) {
      console.error('Print error:', error);
      enqueueSnackbar('Failed to print data', { variant: 'error' });
    }
  };

  const allCutoffDates = useMemo(() => {
    const cutoffMap = {};

    records.forEach(rec => {
      (rec.date_ranges || []).forEach(dr => {
        if (!dr || !dr.payroll_from || !dr.payroll_to) return;

        const key = `${dr.payroll_from}_${dr.payroll_to}`;

        cutoffMap[key] = {
          from: dr.payroll_from,
          to: dr.payroll_to,
        };
      });
    });

    return Object.values(cutoffMap)
      .sort((a, b) => new Date(a.from) - new Date(b.from));
  }, [records]);

  const jumpToNextCell = (record, colIndex) => {
    const nextCol = colIndex;

    const row = document.querySelector(`[data-row-id="${record.id}"]`);
    if (!row) return;

    const cells = row.querySelectorAll('[data-editable-cell="true"]');

    if (cells[nextCol]) {
      cells[nextCol].dispatchEvent(
        new MouseEvent("dblclick", { bubbles: true })
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="200px" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="300px" height={20} sx={{ mb: 3 }} />
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="rectangular" width="200px" height={36} />
            <Skeleton variant="rectangular" width="120px" height={36} />
          </Box>
          {[...Array(5)].map((_, index) => (
            <Skeleton key={index} variant="rectangular" width="100%" height={60} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Paper>
      </Box>
    );
  }

  return (
    <Fade in timeout={600}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 2,
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              sx={{ fontWeight: 'bold', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <ReceiptIcon sx={{ color: '#12C1DC' }} />
              13th Month Pay - Payroll Details
            </Typography>
            <Tooltip title="Refresh data">
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing || !dataLoaded}
                sx={{
                  color: '#12C1DC',
                  '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' }
                }}
              >
                {refreshing ? (
                  <CircularProgress size={20} sx={{ color: '#12C1DC' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link underline="hover" color="inherit" href="/">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="/payroll">
              Payroll
            </Link>
            <Link underline="hover" color="inherit" href="/payroll/13thmonth">
              13th Month Payroll
            </Link>
            <Typography color="text.primary">Payroll Details</Typography>
          </Breadcrumbs>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          </motion.div>
        )}

        {/* Back Button Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={() => window.history.back()}
              sx={{
                borderColor: '#12C1DC',
                color: '#12C1DC',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#0fb8d0',
                  bgcolor: 'rgba(18, 193, 220, 0.1)'
                }
              }}
            >
              ← Back to 13th Month Payroll
            </Button>

            {editing && <Stack direction="row" spacing={2}> <UpdateThirteenthMonthPayButton onClick={async () => {
              await submitChanges()
              toggleEditing(false)
            }} />

              <Button
                variant="outlined"
                onClick={() => {
                  toggleEditing(false)
                  removeChanges()
                  fetchRecords()
                }}
                sx={{
                  borderColor: '#12C1DC',
                  color: '#12C1DC',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#0fb8d0',
                    bgcolor: 'rgba(18, 193, 220, 0.1)'
                  }
                }}
              >
                Cancel
              </Button>

            </Stack>}
          </Box>


        </motion.div>

        {/* Table Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {/* Table Controls */}
            <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderBottom: '1px solid #e5e7eb' }}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    Show
                  </Typography>
                  <TextField
                    select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    size="small"
                    sx={{ minWidth: 80 }}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </TextField>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                    entries
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>

                  {/* Export Buttons */}

                  <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>

                    <Tooltip title="Export CSV">
                      <IconButton
                        size="small"
                        onClick={handlePrintCSV}
                        disabled={!dataLoaded || filteredRecords.length === 0}
                        sx={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 2,
                          '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                        }}
                      >
                        <CsvIcon fontSize="small" sx={{ color: '#6b7280' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Print PDF">


                      <IconButton
                        size="small"
                        onClick={handlePrint}
                        disabled={!dataLoaded || filteredRecords.length === 0}
                        sx={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 2,
                          '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                        }}
                      >
                        <PrintIcon fontSize="small" sx={{ color: '#6b7280' }} />
                      </IconButton>
                    </Tooltip>




                    {/*
                    <Tooltip title="Export PDF">
                      <IconButton
                        size="small"
                        onClick={handleExportPDF}
                        disabled={!dataLoaded || filteredRecords.length === 0}
                        sx={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 2,
                          '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                        }}
                      >
                        <PdfIcon fontSize="small" sx={{ color: '#6b7280' }} />
                      </IconButton>
                    </Tooltip>*/}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Enhanced Table */}


            <TableContainer id='table-container' sx={{ maxHeight: '70vh', overflow: 'auto', position: 'relative' }}>
              {tableLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100vw',
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    borderRadius: 1
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={40} sx={{ color: '#12C1DC' }} />
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        Loading records...
                      </Typography>
                    </Box>
                  </motion.div>
                </Box>
              )}
              <Table
                stickyHeader
                size={isMobile ? "small" : "medium"}
                aria-label="13th month pay table"
              >
                <TableHead>
                  <TableRow sx={{
                    '& .MuiTableCell-root': {
                      backgroundColor: '#f9fafb',
                      borderBottom: '2px solid #e5e7eb'
                    }
                  }}>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        top: 0, 
                        left: 0,        
                        zIndex: 5,   
                        background: '#f9fafb',
                        fontWeight: 600,
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Actions
                    </TableCell>
                    <TableCell
                      sortDirection={orderBy === 'employeeName' ? order : false}
                      sx={{
                        position: 'sticky',
                        fontWeight: 600,
                        top: 0,
                        left: 100,
                        zIndex: 5,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '200px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'employeeName'}
                        direction={orderBy === 'employeeName' ? order : 'asc'}
                        onClick={() => handleRequestSort('employeeName')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        Employee Name
                      </TableSortLabel>
                    </TableCell>
                    {cutoffPeriods.map((cutoffLabel, idx) => {
                      const dateRange = cutoffPeriodToDateRange[cutoffLabel];
                      if (!dateRange) return null;
                      
                      return (
                        <TableCell
                          key={idx}
                          align="right"
                          sx={{
                            fontWeight: 600,
                            bgcolor: '#f9fafb',
                            color: '#374151',
                            fontSize: '0.875rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            minWidth: '140px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {/* Display full cutoff label including year to match filter keys */}
                          {cutoffLabel}
                        </TableCell>
                      );
                    })}
{/*                    <TableCell
                      align="right"
                      sortDirection={orderBy === 'totalBasicPay' ? order : false}
                      sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '150px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'totalBasicPay'}
                        direction={orderBy === 'totalBasicPay' ? order : 'asc'}
                        onClick={() => handleRequestSort('totalBasicPay')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        Total Basic Pay
                      </TableSortLabel>
                    </TableCell>*/}
                    <TableCell
                      align="right"
                      sortDirection={orderBy === 'thirteenthMonthPay' ? order : false}
                      sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '150px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'thirteenthMonthPay'}
                        direction={orderBy === 'thirteenthMonthPay' ? order : 'asc'}
                        onClick={() => handleRequestSort('thirteenthMonthPay')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        13th MP
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                  {/* Filter Row - separate component to prevent re-renders */}
                  <FilterRow
                    cutoffPeriods={cutoffPeriods}
                    appliedFilters={columnFilters}
                    onApplyFilters={handleApplyFilters}
                    onClearFilters={clearAllFilters}
                    hasActiveFilters={hasActiveFilters}
                    isPending={isPending}
                  />
                </TableHead>
                <TableBody>
                  {!dataLoaded && records.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
                          No Data Loaded
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                          Please select a store and year, then click "Generate" to load 13th month pay records.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : paginatedRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
                          No Records Found
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                          {hasActiveFilters ? 'Try adjusting your filters.' : 'No records available for the selected criteria.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <AnimatePresence>
                      {paginatedRecords.map((record, idx) => {
                        const isEvenRow = idx % 2 === 0;
                        return (
                          <motion.tr
                            data-row-id={record.id}
                            key={record._id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2, delay: idx * 0.05 }}
                            style={{
                              display: 'table-row',
                              backgroundColor: isEvenRow ? '#ffffff' : '#f8f9fa'
                            }}
                          >
                            <TableCell
                              sx={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 4,
                                background: '#fff',
                                minWidth: '60px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenPopover(e, record);
                                  }}
                                  sx={{
                                    color: '#6b7280',
                                    '&:hover': { bgcolor: 'rgba(107, 114, 128, 0.1)' }
                                  }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                position: 'sticky',
                                left: 100,
                                zIndex: 2,
                                background: '#fff',
                                whiteSpace: 'nowrap',
                              }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon sx={{ color: '#12C1DC', fontSize: '1.2rem' }} />
                                <Box>
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                  >
                                    {record.employeeName?.toUpperCase()}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {record.employeeId}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>

                            {cutoffPeriods.map((cutoffLabel, cIdx) => {
                              // Use getCutoffPeriod for consistent label generation
                              // Find the actual index in record's date_ranges array (not cutoffPeriods index)
                              // This is critical because cutoffPeriods is sorted chronologically, but date_ranges
                              // may be in a different order, so indices won't match
                              const dateRangeIndex = record.date_ranges?.findIndex(dr => getCutoffPeriod(dr) === cutoffLabel) ?? -1;
                              const match = dateRangeIndex >= 0 ? record.date_ranges[dateRangeIndex] : null;
                              
                              // Use the actual date_ranges index for getUpdatedBasicPay, not the cutoffPeriods index
                              // Only get updated value if the date_range exists in this record
                              const actualIndex = dateRangeIndex >= 0 ? dateRangeIndex : null;
                              let editedValue = actualIndex !== null ? getUpdatedBasicPay(record.id, actualIndex) : null;
                              const value = match?.basic_pay ?? 0;
                              const updatedValue = actualIndex !== null ? getUpdatedBasicPay(record.id, actualIndex) : null;
                              let displayValue = updatedValue ? updatedValue : Number(value);

                              if (editedValue) {
                                displayValue = editedValue;
                              }
                              
                              return (
                                <TableCell key={cIdx} align="right" sx={{ whiteSpace: 'nowrap' }}>
                                  <EditableComponent
                                    editable={payrollStatus === 'processing'}
                                    text={
                                      displayValue === 0
                                        ? '0'
                                        : displayValue.toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })
                                    }

                                    onChange={(newValue) => {
                                      const parsedValue = parseFloat(newValue) || 0;

                                      setRecords((records) => {
                                        return records.map((r) => {
                                          if (r.id !== record.id) return r;

                                          // Update only the affected date_range
                                          const newDateRanges = r.date_ranges.map((dr) =>
                                            getCutoffPeriod(dr) === cutoffLabel
                                              ? { ...dr, basic_pay: parsedValue }
                                              : dr
                                          );

                                          const dateRangeExists = newDateRanges.some(
                                            (dr) => getCutoffPeriod(dr) === cutoffLabel
                                          );

                                          // Track if we're adding a new entry
                                          let newEntryAdded = false;
                                          let newEntryIndex = -1;
                                          
                                          if (!dateRangeExists) {
                                            const cutoff = allCutoffDates.find(
                                              (co) =>
                                                getCutoffPeriod({ payroll_from: co.from, payroll_to: co.to }) ===
                                                cutoffLabel
                                            );

                                            if (cutoff) {
                                              newDateRanges.push({
                                                payroll_from: cutoff.from,
                                                payroll_to: cutoff.to,
                                                basic_pay: parsedValue,
                                              });
                                              newEntryAdded = true;
                                              newEntryIndex = newDateRanges.length - 1;
                                            }
                                          }

                                          // Recalculate totals for this row
                                          const totalBasicPay = newDateRanges.reduce(
                                            (sum, dr) => sum + (dr.basic_pay || 0),
                                            0
                                          );
                  
                                          const thirteenthMonthPay = totalBasicPay;

                                          // Find the actual index in the updated date_ranges array
                                          const updatedDateRangeIndex = newDateRanges.findIndex(
                                            (dr) => getCutoffPeriod(dr) === cutoffLabel
                                          );
                                          
                                          // Determine the final index and get the corresponding date_range
                                          // Use the new entry index if we just added one, otherwise use the found index
                                          // Only proceed with update if we have a valid index
                                          const finalIndex = updatedDateRangeIndex >= 0 
                                            ? updatedDateRangeIndex 
                                            : (newEntryAdded ? newEntryIndex : -1);
                                          
                                          // Only call updateThirteenthMonthPay if we have a valid date_range
                                          if (finalIndex >= 0 && newDateRanges[finalIndex]) {
                                            const targetDateRange = newDateRanges[finalIndex];
                                            
                                            // Use the actual date_ranges index for updateThirteenthMonthPay
                                            // Use the same date_range for both index and dates to ensure consistency
                                            updateThirteenthMonthPay(
                                              record._id,
                                              finalIndex,
                                              parsedValue,
                                              totalBasicPay,
                                              totalBasicPay,
                                              targetDateRange.payroll_from,
                                              targetDateRange.payroll_to
                                            );
                                          }
                                          toggleEditing(true)
                                          return {
                                            ...r,
                                            date_ranges: newDateRanges,
                                            totalBasicPay,
                                            thirteenthMonthPay,
                                          };
                                        });
                                      });
                                    }}
                                    onTab={() => jumpToNextCell(record, cIdx)}
                                  />
                                </TableCell>

                              );
                            })}

                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-end',
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={{ color: '#6b7280', fontSize: '1rem' }}
                                >
                                  ₱
                                </Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={{ color: '#0fb8d0' }}
                                >
                                  {record?.thirteenthMonthPay.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </Typography>
                              </Box>
                            </TableCell>
                           {/* <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-end',
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={{ color: '#6b7280', fontSize: '1rem' }}
                                >
                                  ₱
                                </Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={{ color: '#0fb8d0' }}
                                >
                                  {(record?.thirteenthMonthPay || 0).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </Typography>
                              </Box>
                            </TableCell>*/}
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  )
                  }
                </TableBody>
                {/* Sticky Totals Row in TableFooter */}
                {dataLoaded && filteredRecords.length > 0 && !isPending && (
                  <TableFooter
                    sx={{
                      position: 'sticky',
                      bottom: 0,
                      zIndex: 4,
                      '& .MuiTableCell-root': {
                        backgroundColor: '#12C1DC',
                        borderTop: '2px solid #0fb8d0',
                      }
                    }}
                  >
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 600, 
                          color: 'white', 
                          position: 'sticky', 
                          left: 0, 
                          zIndex: 5, 
                          background: '#12C1DC',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Total
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: 'white',
                          position: 'sticky',
                          zIndex: 4,
                          background: '#12C1DC',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        -
                      </TableCell>
                      {cutoffPeriods.map((cutoffLabel, idx) => {
                        // sum across filtered employees for this cutoff (respects active filters)
                        const total = filteredRecords.reduce((sum, record) => {
                          const match = record.date_ranges?.find(inner => getCutoffPeriod(inner) === cutoffLabel);
                          return sum + (match?.basic_pay ?? 0);
                        }, 0);

                        return (
                          <TableCell
                            key={idx}
                            align="right"
                            sx={{ fontWeight: 600, color: 'white', background: '#12C1DC', whiteSpace: 'nowrap' }}
                          >
                            ₱{total.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })} 
                          </TableCell>
                        );
                      })}

                      <TableCell align="right" sx={{ fontWeight: 700, color: 'white', background: '#12C1DC', whiteSpace: 'nowrap' }}>
                        ₱{records.reduce((sum, record) => {
                          return sum + (Number(record.thirteenthMonthPay) ?? 0);
                        }, 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      {/*<TableCell align="right" sx={{ fontWeight: 700, color: 'white', background: '#12C1DC', whiteSpace: 'nowrap' }}>
                        ₱{(
                          filteredRecords.reduce(
                            (sum, record) => sum + (record.thirteenthMonthPay ?? 0),
                            0
                          ) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })} 
                      </TableCell>*/}
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </TableContainer>

            {/* Pagination - Always visible */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              gap: { xs: 2, sm: 0 }
            }}>
              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                {dataLoaded && filteredRecords.length > 0 ? (
                  <>
                    Showing {((page - 1) * rowsPerPage) + 1} to {Math.min(page * rowsPerPage, filteredRecords.length)} of {filteredRecords.length} entries
                    {hasActiveFilters && ` (filtered)`}
                  </>
                ) : (
                  'No records to display'
                )}
              </Typography>
              <Pagination
                count={totalPages || 1}
                page={page}
                onChange={handlePageChange}
                size={isMobile ? "small" : "medium"}
                disabled={!dataLoaded || filteredRecords.length === 0}
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    '&.Mui-selected': {
                      backgroundColor: '#12C1DC',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#0FABDB'
                      }
                    }
                  }
                }}
              />
            </Box>
          </Paper>
        </motion.div> :


        {/* Payslip Dialog */}
        {
          payslipDialogOpen && selectedEmployee && (
            <ThirteenthMonthPayslipDialog
              open={payslipDialogOpen}
              onClose={handleClosePayslipDialog}
              employee={selectedEmployee}
              fromDate={fromDate}
              toDate={toDate}
              store={selectedStore}
            />
          )
        }


        {/* Edit Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={handleCancelEdit}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: 'white',
            py: 2
          }}>
            <Typography variant="h6" fontWeight="bold">
              Edit Payroll Record
            </Typography>
            <IconButton
              onClick={handleCancelEdit}
              sx={{
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0 }}>
            {editData && (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#2c3e50' }}>
                  Editing: {editData.employeeName} ({editData.employeeId})
                </Typography>

                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Month</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, color: '#374151' }}>1st Cutoff</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, color: '#374151' }}>2nd Cutoff</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, color: '#374151' }}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>October 2024</TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={editData.OCT1631 || 0}
                            onChange={(e) => handleEditDataChange('OCT1631', e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          -
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          ₱{(editData.OCT1631 || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:nth-of-type(even)': { bgcolor: '#f8f9fa' } }}>
                        <TableCell sx={{ fontWeight: 600 }}>November 2024</TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={editData.NOV115 || 0}
                            onChange={(e) => handleEditDataChange('NOV115', e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={editData.NOV1630 || 0}
                            onChange={(e) => handleEditDataChange('NOV1630', e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          ₱{((editData.NOV115 || 0) + (editData.NOV1630 || 0)).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>December 2024</TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={editData.DEC115 || 0}
                            onChange={(e) => handleEditDataChange('DEC115', e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={editData.DEC1631 || 0}
                            onChange={(e) => handleEditDataChange('DEC1631', e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          ₱{((editData.DEC115 || 0) + (editData.DEC1631 || 0)).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:nth-of-type(even)': { bgcolor: '#f8f9fa' } }}>
                        <TableCell sx={{ fontWeight: 600 }}>January 2025</TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={editData.JAN115 || 0}
                            onChange={(e) => handleEditDataChange('JAN115', e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={editData.JAN1631 || 0}
                            onChange={(e) => handleEditDataChange('JAN1631', e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          ₱{((editData.JAN115 || 0) + (editData.JAN1631 || 0)).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>February 2025</TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={editData.FEB115 || 0}
                            onChange={(e) => handleEditDataChange('FEB115', e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={editData.FEB1628 || 0}
                            onChange={(e) => handleEditDataChange('FEB1628', e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          ₱{((editData.FEB115 || 0) + (editData.FEB1628 || 0)).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </TableCell>
                      </TableRow>
                      {/* Total Row */}
                      <TableRow sx={{
                        bgcolor: '#12C1DC',
                        '& .MuiTableCell-root': {
                          color: 'white',
                          fontWeight: 'bold',
                          borderTop: '2px solid #0fb8d0'
                        }
                      }}>
                        <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                        <TableCell align="center">-</TableCell>
                        <TableCell align="center">-</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          ₱{[
                            editData.OCT1631 || 0,
                            editData.NOV115 || 0,
                            editData.NOV1630 || 0,
                            editData.DEC115 || 0,
                            editData.DEC1631 || 0,
                            editData.JAN115 || 0,
                            editData.JAN1631 || 0,
                            editData.FEB115 || 0,
                            editData.FEB1628 || 0
                          ].reduce((sum, val) => sum + val, 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={handleCancelEdit}
              variant="outlined"
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#9ca3af',
                  bgcolor: '#f9fafb'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              variant="contained"
              sx={{
                bgcolor: '#f59e0b',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#d97706' }
              }}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Transfer Employee Dialog */}
        <Dialog
          open={transferDialogOpen}
          onClose={handleCancelTransfer}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            py: 2
          }}>
            <Typography variant="h6" fontWeight="bold">
              Transfer Employee
            </Typography>
            <IconButton
              onClick={handleCancelTransfer}
              sx={{
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {selectedEmployeeForTransfer && (
              <Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Transfer <strong>{selectedEmployeeForTransfer.employeeName}</strong> ({selectedEmployeeForTransfer.employeeId}) to another store.
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
                  This action will move the employee to a different store and update their payroll records accordingly.
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Current Store: <span style={{ color: '#3b82f6' }}>{selectedEmployeeForTransfer.store}</span>
                  </Typography>

                  <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
                    <InputLabel htmlFor="transfer-store-select">Select New Store</InputLabel>
                    <Select
                      value={selectedTransferStore}
                      onChange={(e) => setSelectedTransferStore(e.target.value)}
                      label="Select New Store"
                      inputProps={{
                        name: 'store',
                        id: 'transfer-store-select',
                      }}
                    >
                      {ALL_STORES
                        .filter(store => store !== selectedEmployeeForTransfer.store) // Exclude current store
                        .map((store) => (
                          <MenuItem key={store} value={store}>
                            {store}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>

                {selectedTransferStore && (
                  <Box sx={{
                    p: 2,
                    bgcolor: '#f0f9ff',
                    border: '1px solid #0ea5e9',
                    borderRadius: 1,
                    mb: 2
                  }}>
                    <Typography variant="body2" sx={{ color: '#0ea5e9', fontWeight: 600 }}>
                      ✓ Employee will be transferred to: <strong>{selectedTransferStore}</strong>
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={handleCancelTransfer}
              variant="outlined"
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#9ca3af',
                  bgcolor: '#f9fafb'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmTransfer}
              variant="contained"
              disabled={!selectedTransferStore}
              sx={{
                bgcolor: selectedTransferStore ? '#3b82f6' : '#9ca3af',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: selectedTransferStore ? '#2563eb' : '#9ca3af'
                }
              }}
            >
              {selectedTransferStore ? 'Confirm Transfer' : 'Select Store'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Remove Employee Dialog */}
        <Dialog
          open={removeDialogOpen}
          onClose={handleCancelRemove}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            py: 2
          }}>
            <Typography variant="h6" fontWeight="bold">
              Remove Employee
            </Typography>
            <IconButton
              onClick={handleCancelRemove}
              sx={{
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {selectedEmployeeForRemove && (
              <Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Are you sure you want to remove <strong>{selectedEmployeeForRemove.employeeName}</strong> ({selectedEmployeeForRemove.employeeId}) from this payroll?
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
                  This action will permanently remove the employee from this 13th month payroll record. This action cannot be undone.
                </Typography>

                <Box sx={{
                  p: 2,
                  bgcolor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 1,
                  mb: 2
                }}>
                  <Typography variant="body2" sx={{ color: '#dc2626', fontWeight: 600 }}>
                    ⚠️ Warning: This will remove the employee's 13th month pay calculation from this payroll period.
                  </Typography>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Employee Details:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    • Name: {selectedEmployeeForRemove.employeeName}
                  </Typography>
                  {/*                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    • ID: {selectedEmployeeForRemove.employeeId}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    • Store: {selectedEmployeeForRemove.store}
                  </Typography>*/}
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    • 13th Month Pay: ₱{selectedEmployeeForRemove.thirteenthMonthPay.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              onClick={handleCancelRemove}
              variant="outlined"
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#9ca3af',
                  bgcolor: '#f9fafb'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRemove}
              variant="contained"
              sx={{
                bgcolor: '#ef4444',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#dc2626' }
              }}
            >
              Remove Employee
            </Button>
          </DialogActions>
        </Dialog>

        {/* Popover for employee actions */}
        <Popover
          open={Boolean(popoverAnchor)}
          anchorEl={popoverAnchor}
          onClose={handleClosePopover}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box sx={{ p: 1, minWidth: 150 }}>
            {/*            <MenuItem onClick={() => selectedEmployeeForPopover && handleEditRecord(selectedEmployeeForPopover)}>
              <EditIcon sx={{ mr: 1, fontSize: 18 }} />
              Edit
            </MenuItem>*/}
            {/*            <MenuItem onClick={() => selectedEmployeeForPopover && handleTransferEmployee(selectedEmployeeForPopover)}>
              <TransferIcon sx={{ mr: 1, fontSize: 18 }} />
              Transfer
            </MenuItem>*/}
            <MenuItem onClick={() => selectedEmployeeForPopover && handleRemoveEmployee(selectedEmployeeForPopover)} sx={{ color: 'error.main' }}>
              <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
              Remove Employee
            </MenuItem>
          </Box>
        </Popover>
      </Box >
    </Fade >
  );
};

export default ViewThirteenthMonthPayroll;

